import { rootApi } from './baseApi';
import { Sensor, SensorChannel, SensorType } from "../types";

interface UpdateSensorRequest {
  sensorId: number;
  sensorName: string;
}

interface UserPreference {
  user_id: number;
  preference_key: string;
  preference_value: string;
}

interface AddSensorResponse {
  id?: number;
  serialNo?: number;
  name?: string;
  type?: string;
  message?: string;
}

interface ZoneSensor {
  sensor_id: number;
  sensor_name: string;
  sensor_serialNo: number;
  zone_ids: string;
}

export const sensorApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getTypes: builder.query<SensorType[], void>({
      query: () => ({
        url: "/types",
        method: "GET"
      }),
      providesTags: ['Sensors']
    }),

    getSensorList: builder.query<Sensor[], void>({
      query: () => ({
        url: "/sensors",
        method: "GET"
      }),
      providesTags: ['Sensors'],
      // Cache for 5 minutes since sensor list doesn't change often
      keepUnusedDataFor: 300,
      transformResponse: (response: any[]) => {
        return response.map(sensor => ({
          ...sensor,
          id: sensor.id != null ? Number(sensor.id) : sensor.id,
          serialNo: sensor.serialNo != null ? Number(sensor.serialNo) : sensor.serialNo,
          type: sensor.type != null ? String(sensor.type) : sensor.type,
          firmware_version: sensor.firmware,
          channels: Array.isArray(sensor.channels) ? sensor.channels : [],
        }));
      }
    }),

    addSensor: builder.mutation<AddSensorResponse, any>({
      query: (newSensor) => ({
        url: "/sensors",
        method: "POST",
        body: newSensor
      }),
      transformResponse: (response: any): AddSensorResponse => ({
        id: response.id != null ? Number(response.id) : undefined,
        serialNo: response.serialNo != null ? Number(response.serialNo) : undefined,
        name: response.name,
        type: response.type != null ? String(response.type) : undefined,
        message: response.message,
      }),
      // Do not invalidate 'Sensors': the component patches the cache via updateQueryData immediately.
      // A background refetch triggered here can finish out of order when adding sensors rapidly,
      // overwriting a subsequent optimistic update and making the new sensor vanish temporarily.
    }),

    updateSensor: builder.mutation({
      query: (updateData: UpdateSensorRequest) => ({
        url: `/sensors/${updateData.sensorId}`,
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: {
          sensorId: updateData.sensorId,
          sensorName: updateData.sensorName
        }
      }),
      invalidatesTags: ['Sensors'],
    }),

    deleteSensor: builder.mutation<void, Pick<Sensor, 'id'>>({
      query: (sensor) => ({
        url: `/sensors/${sensor.id}`,
        method: "DELETE"
      }),
      async onQueryStarted(sensor, { dispatch, queryFulfilled }) {
        const sid = Number(sensor.id);
        const patch = dispatch(
          sensorApi.util.updateQueryData('getSensorList', undefined, (draft) => {
            const idx = draft.findIndex((s) => Number(s.id) === sid);
            if (idx !== -1) draft.splice(idx, 1);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      // Do not invalidate 'Sensors': concurrent GET /sensors refetches can finish out of order
      // after multiple deletes and overwrite the cache with stale lists. Optimistic removal above is enough.
      invalidatesTags: ['Zones', 'ZoneSensors'],
    }),

    setSensorServiceStatus: builder.mutation<
      { status: string; message: string; is_out_of_service: boolean },
      { sensorId: number; isOutOfService: boolean }
    >({
      query: ({ sensorId, isOutOfService }) => ({
        url: `/sensors/${sensorId}/service-status`,
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: { is_out_of_service: isOutOfService }
      }),
      async onQueryStarted({ sensorId, isOutOfService }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          sensorApi.util.updateQueryData('getSensorList', undefined, (draft) => {
            const target = draft.find((s) => Number(s.id) === Number(sensorId));
            if (target) target.is_out_of_service = isOutOfService;
          })
        );
        try {
          const { data: result } = await queryFulfilled;
          // Confirm from server response — avoids a full refetch overwriting with stale data
          dispatch(
            sensorApi.util.updateQueryData('getSensorList', undefined, (draft) => {
              const target = draft.find((s) => Number(s.id) === Number(sensorId));
              if (target) target.is_out_of_service = result.is_out_of_service;
            })
          );
        } catch {
          patch.undo();
        }
      },
    }),

    updateSensorCalibration: builder.mutation<
      { status: string; message: string; calibration_date: string | null },
      { sensorId: number; calibrationDate: string | null }
    >({
      query: ({ sensorId, calibrationDate }) => ({
        url: `/sensors/${sensorId}/calibration`,
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: { calibration_date: calibrationDate }
      }),
      async onQueryStarted({ sensorId, calibrationDate }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          sensorApi.util.updateQueryData('getSensorList', undefined, (draft) => {
            const target = draft.find((s) => Number(s.id) === Number(sensorId));
            if (target) {
              target.calibration_date = calibrationDate;
              target.calibration_due = calibrationDate
                ? new Date(new Date(calibrationDate).setMonth(new Date(calibrationDate).getMonth() + 12)).toISOString().slice(0, 10)
                : null;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    // Removed duplicate getLiveData - use sensorDataApi instead

    getZoneSensors: builder.query<ZoneSensor[], number>({
      query: (zoneId) => ({
        url: `/zones/${zoneId}/sensors`,
        method: 'GET'
      })
    }),

    getUserPreferences: builder.query<UserPreference[], { userId: number }>({
      query: ({ userId }) => ({
        url: `/user-preferences?userId=${userId}`,
        method: 'GET',
      }),
      providesTags: ['UserPreferences'],
    }),

    upsertUserPreference: builder.mutation<{ ok: boolean }, { userId: number; preference_key: string; preference_value: string }>({
      query: (body) => ({
        url: '/user-preferences',
        method: 'POST',
        body,
      }),
      async onQueryStarted({ userId, preference_key, preference_value }, { dispatch, queryFulfilled, getState }) {
        const cached = sensorApi.endpoints.getUserPreferences.select({ userId })(getState());
        const hadData = cached.status === 'fulfilled' && Array.isArray(cached.data);

        const patchResult = hadData
          ? dispatch(
              sensorApi.util.updateQueryData('getUserPreferences', { userId }, (draft) => {
                const trimmed = String(preference_value ?? '').trim();
                const idx = draft.findIndex((p) => p.preference_key === preference_key);
                if (!trimmed) {
                  if (idx >= 0) draft.splice(idx, 1);
                } else if (idx >= 0) {
                  draft[idx].preference_value = trimmed;
                } else {
                  draft.push({ user_id: userId, preference_key, preference_value: trimmed });
                }
              }),
            )
          : undefined;

        try {
          await queryFulfilled;
          // No cache yet: load full prefs from server once (don't rely on stale invalidation-only UX)
          if (!hadData) dispatch(sensorApi.util.invalidateTags(['UserPreferences']));
        } catch {
          patchResult?.undo();
        }
      },
    }),

    getChannelConfig: builder.query<SensorChannel[], number>({
      query: (sensorId) => ({ url: `/sensors/${sensorId}/channels`, method: 'GET' }),
      providesTags: (_, __, sensorId) => [{ type: 'SensorChannels', id: sensorId }],
    }),

    upsertChannelConfig: builder.mutation<{ ok: boolean }, { sensorId: number; channels: Partial<SensorChannel>[] }>({
      query: ({ sensorId, channels }) => ({
        url: `/sensors/${sensorId}/channels`,
        method: 'PUT',
        body: channels,
      }),
      invalidatesTags: (_, __, { sensorId }) => [{ type: 'SensorChannels', id: sensorId }, 'Sensors'],
    }),

    deleteChannelConfig: builder.mutation<{ ok: boolean }, number>({
      query: (sensorId) => ({ url: `/sensors/${sensorId}/channels`, method: 'DELETE' }),
      invalidatesTags: (_, __, sensorId) => [{ type: 'SensorChannels', id: sensorId }, 'Sensors'],
    }),

    // Super admin only. probeType picks which firmware build (internal or
    // external thermistor) the firmware server hands this sensor; the probe is
    // fixed per build, there is no runtime probe switch any more.
    updateFirmware: builder.mutation<
      { ok: boolean; topic: string; message: string; probeType: 'internal' | 'external'; targetVersion: string },
      {
        sensorSerialNumber: number;
        baseUnitSerialNumbers: number[];
        wifi: { ssid: string; password: string };
        probeType: 'internal' | 'external';
      }
    >({
      query: (body) => ({
        url: '/update-firmware',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Sensors'],
    }),
  }),
});

// Export the hooks
export const {
  useGetTypesQuery,
  useGetSensorListQuery,
  useAddSensorMutation,
  useUpdateSensorMutation,
  useDeleteSensorMutation,
  useSetSensorServiceStatusMutation,
  useUpdateSensorCalibrationMutation,
  useGetZoneSensorsQuery,
  useGetUserPreferencesQuery,
  useUpsertUserPreferenceMutation,
  useGetChannelConfigQuery,
  useUpsertChannelConfigMutation,
  useDeleteChannelConfigMutation,
  useUpdateFirmwareMutation,
} = sensorApi;